import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function DyvysWatchPage() {
  const { episodeId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [episode, setEpisode] = useState(null);
  const [series, setSeries] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);

  useEffect(() => {
    if (!user) {
      toast.error("Veuillez vous connecter");
      navigate("/login");
      return;
    }
    fetchEpisodeAndCheckAccess();
  }, [episodeId, user]);

  const fetchEpisodeAndCheckAccess = async () => {
    try {
      // Récupérer d'abord tous les épisodes pour trouver le bon
      const seriesResponse = await fetch(`${API_URL}/api/dyvys/series`);
      const seriesData = await seriesResponse.json();
      
      let foundEpisode = null;
      let foundSeries = null;

      for (const s of seriesData.series) {
        const detailResponse = await fetch(`${API_URL}/api/dyvys/series/${s.series_id}`);
        const detailData = await detailResponse.json();
        
        const ep = detailData.episodes.find(e => e.episode_id === episodeId);
        if (ep) {
          foundEpisode = ep;
          foundSeries = detailData.series;
          break;
        }
      }

      if (!foundEpisode) {
        toast.error("Épisode introuvable");
        navigate("/dyvys");
        return;
      }

      setEpisode(foundEpisode);
      setSeries(foundSeries);

      // Vérifier l'accès
      const accessResponse = await fetch(`${API_URL}/api/dyvys/check-access`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          item_type: "episode",
          item_id: episodeId,
          user_id: user.user_id
        })
      });
      
      const accessData = await accessResponse.json();
      
      if (!accessData.has_access && !foundEpisode.is_free) {
        toast.error("Vous devez acheter cet épisode pour le regarder");
        navigate(`/dyvys/series/${foundSeries.series_id}`);
        return;
      }

      setHasAccess(true);
    } catch (error) {
      console.error("Erreur:", error);
      toast.error("Erreur de chargement");
      navigate("/dyvys");
    } finally {
      setLoading(false);
    }
  };

  const extractYouTubeVideoId = (url) => {
    if (!url) return null;
    
    // Si c'est déjà juste l'ID
    if (url.length === 11 && !url.includes('/') && !url.includes('?')) {
      return url;
    }
    
    // Pattern pour les URLs YouTube
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
      /^([a-zA-Z0-9_-]{11})$/
    ];
    
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) return match[1];
    }
    
    return null;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-purple-500 mx-auto mb-4"></div>
          <p className="text-lg">Chargement de la vidéo...</p>
        </div>
      </div>
    );
  }

  if (!episode || !hasAccess) {
    return null;
  }

  const videoId = extractYouTubeVideoId(episode.video_url);

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <header className="border-b border-gray-800 bg-black/90 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Button
              onClick={() => navigate(`/dyvys/series/${series.series_id}`)}
              variant="ghost"
              className="text-gray-300 hover:text-white hover:bg-gray-800"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour à la série
            </Button>
            <div className="text-right">
              <p className="text-sm text-gray-400">{series.title}</p>
              <p className="text-white font-semibold">Épisode {episode.episode_number}: {episode.title}</p>
            </div>
          </div>
        </div>
      </header>

      {/* Video Player */}
      <main className="container mx-auto px-4 py-8">
        <div className="max-w-6xl mx-auto">
          {episode.video_type === "youtube" && videoId ? (
            <div className="relative w-full" style={{ paddingBottom: "56.25%" }}>
              <iframe
                className="absolute top-0 left-0 w-full h-full rounded-lg shadow-2xl"
                src={`https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`}
                title={episode.title}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
          ) : episode.video_type === "upload" ? (
            <video
              className="w-full rounded-lg shadow-2xl"
              controls
              autoPlay
              src={episode.video_url}
            >
              Votre navigateur ne supporte pas la lecture vidéo.
            </video>
          ) : (
            <div className="bg-gray-900 rounded-lg p-12 text-center">
              <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
              <p className="text-xl text-gray-300">Impossible de lire cette vidéo</p>
              <p className="text-gray-500 mt-2">Format de vidéo non supporté</p>
            </div>
          )}

          {/* Episode Info */}
          <div className="mt-8 bg-gradient-to-r from-gray-900 to-purple-900/30 rounded-lg p-6">
            <h1 className="text-3xl font-bold mb-2">
              Épisode {episode.episode_number}: {episode.title}
            </h1>
            {episode.description && (
              <p className="text-gray-300 leading-relaxed">
                {episode.description}
              </p>
            )}
            {episode.duration_minutes && (
              <p className="text-gray-400 mt-4 text-sm">
                Durée: {episode.duration_minutes} minutes
              </p>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
